import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import '../css/Home.css';

import bigONotationImage from '../../assets/use-cases/bigo_notation_prompt.png';
import calculusImage from '../../assets/use-cases/calculus_prompt.png';
import physicsMechanicsImage from '../../assets/use-cases/physics_mechanics_prompt.png';
import truthTablesImage from '../../assets/use-cases/truth_table_prompt.png';
import statisticsImage from '../../assets/use-cases/statistics_prompt.png';
import relationalAlgebraImage from '../../assets/use-cases/relational_algebra_prompt.png';
import vectorCalculusImage from '../../assets/use-cases/vector_calculus_prompt.png';
import differentialEquationsImage from '../../assets/use-cases/differential_equations_prompt.png';
import laplaceTransformsImage from '../../assets/use-cases/laplace_transforms_prompt.png';

import keyboardsImage from '../../assets/features/keyboards.png';
import darkModeImage from '../../assets/features/dark_mode.png';
import chatrenderImage from '../../assets/features/chat_rendering.png';
import chatgptImage from '../../assets/features/chatgpt_int.png';
import toolTipsImage from '../../assets/features/tool_tips.png';
import memoryRecallImage from '../../assets/features/memory_recall.png';

function Home() {
  const navigate = useNavigate();
  const updatesGridRef = useRef(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  const featureSlides = [
    {
      id: 1,
      title: "ChatGPT Integration",
      description: "Extends your existing ChatGPT account, toggled on and off with a single click",
      image: chatgptImage
    },
    {
      id: 2,
      title: "In-chat Rendering",
      description: "See your equations render instantly in the chat",
      image: chatrenderImage
    },
    {
      id: 3,
      title: "Keyboard Support",
      description: "Multiple keyboards supporting different subjects and symbols",
      image: keyboardsImage
    },
    {
      id: 4,
      title: "Memory Recall",
      description: "Easy access to your previous equations",
      image: memoryRecallImage
    },
    {
      id: 5,
      title: "Tool Tips",
      description: "Labeled buttons for each keyboard subject",
      image: toolTipsImage
    },
    {
      id: 6,
      title: "Dark Mode Support",
      description: "Automatically adapts to your system settings",
      image: darkModeImage
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prevSlide) => 
        prevSlide === featureSlides.length - 1 ? 0 : prevSlide + 1
      );
    }, 6000); 

    return () => clearInterval(interval);
  }, [featureSlides.length]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await getCurrentUser();
        setIsAuthenticated(true);
        navigate('/dashboard');
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const handleSignIn = () => {
    if (!isAuthenticated) {
      navigate('/signin');
    } else {
      navigate('/dashboard');
    }
  };

  const scrollUpdates = (direction) => {
    if (updatesGridRef.current) {
      const cardWidth = 700; 
      const gap = 32; 
      const scrollAmount = cardWidth + gap;
      const currentScroll = updatesGridRef.current.scrollLeft;
      updatesGridRef.current.scrollTo({
        left: currentScroll + (direction === 'right' ? scrollAmount : -scrollAmount),
        behavior: 'smooth'
      });
    }
  };

  if (isChecking) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="landing-container">
      <nav className="landing-nav">
        <div className="nav-content">
          <div className="logo">Equation Live</div>
          <div className="nav-buttons">
            <button 
              className="nav-button primary" 
              onClick={handleSignIn}
              disabled={isChecking}
            >
              {isAuthenticated ? 'Dashboard' : 'Sign In'}
            </button>
          </div>
        </div>
      </nav>

      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            Expand your ChatGPT vocabulary with
            <span className="gradient-text">Equation Live</span>
          </h1>
          <p className="hero-subtitle">
            Edit and render LaTeX directly in your ChatGPT conversations with our built in keyboard and editor.
          </p>
          <div className="hero-buttons">
            <button className="cta-button primary" onClick={() => window.open(process.env.REACT_APP_CHROME_WEBSTORE_EXTENSION_LINK)}>
              Install Extension
            </button>
            <button className="cta-button secondary" onClick={() => navigate('/signup')}>
              Sign Up
            </button>
          </div>
        </div>
        <div className="hero-image">
          <div className="showcase-container">
            <div className="showcase-frame">
              <div className="slideshow-container">
                {featureSlides.map((slide, index) => (
                  <div 
                    key={slide.id}
                    className={`slide ${index === currentSlide ? 'active' : ''}`}
                  >
                    <img 
                      src={slide.image} 
                      alt={slide.title}
                      className="slide-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <div className="slide-overlay">
                      <div className="slide-content">
                        <h3>{slide.title}</h3>
                        <p>{slide.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="slideshow-indicators">
                  {featureSlides.map((_, index) => (
                    <button
                      key={index}
                      className={`indicator ${index === currentSlide ? 'active' : ''}`}
                      onClick={() => setCurrentSlide(index)}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="how-it-works">
        <h2 className="section-title">How It Works</h2>
        <div className="steps-container">
          <div className="step-card">
            <div className="step-number">1</div>
            <h3>Install the Extension</h3>
            <p>Install the extension from the Chrome Web Store.</p>
          </div>
          <div className="step-card">
            <div className="step-number">2</div>
            <h3>Sign up</h3>
            <p>Create your account for free using your email.</p>
          </div>
          <div className="step-card">
            <div className="step-number">3</div>
            <h3>Start Learning</h3>
            <p>Sign in on ChatGPT and start chatting with Equation Live.</p>
          </div>
        </div>
      </section>

      <section className="updates-section">
        <h2>Prompt Cases</h2>
        <div className="updates-container">
          <button 
            className="scroll-button left" 
            onClick={() => scrollUpdates('left')}
            aria-label="Scroll left"
          >
            ←
          </button>
          <div className="updates-grid" ref={updatesGridRef}>
            <div className="update-card">
              <h3>Calculus</h3>
              <div className="update-image-container">
                <img src={calculusImage} alt="Calculus Example" className="update-image" />
              </div>
            </div>
            <div className="update-card">
              <h3>Physics (Mechanics)</h3>
              <div className="update-image-container">
                <img src={physicsMechanicsImage} alt="Physics(Mechanics) Example" className="update-image" />
              </div>
            </div>
            <div className="update-card">
              <h3>Big-O Notation</h3>
              <div className="update-image-container">
                <img src={bigONotationImage} alt="Big-O Notation Example" className="update-image" />
              </div>
            </div>
            <div className="update-card">
              <h3>Statistics</h3>
              <div className="update-image-container">
                <img src={statisticsImage} alt="Statistics Example" className="update-image" />
              </div>
            </div>
            <div className="update-card">
              <h3>Differential Equations</h3>
              <div className="update-image-container">
                <img src={differentialEquationsImage} alt="Differential Equations Example" className="update-image" />
              </div>
            </div>
            <div className="update-card">
              <h3>Relational Algebra</h3>
              <div className="update-image-container">
                <img src={relationalAlgebraImage} alt="Relational Algebra Example" className="update-image" />
              </div>
            </div>
            <div className="update-card">
              <h3>Truth Tables</h3>
              <div className="update-image-container">
                <img src={truthTablesImage} alt="Truth Tables Example" className="update-image" />
              </div>
            </div>
            <div className="update-card">
              <h3>Laplace Transforms</h3>
              <div className="update-image-container">
                <img src={laplaceTransformsImage} alt="Laplace Transforms Example" className="update-image" />
              </div>
            </div>
            <div className="update-card">
              <h3>Vector Calculus</h3>
              <div className="update-image-container">
                <img src={vectorCalculusImage} alt="Vector Calculus Example" className="update-image" />
              </div>
            </div>
          </div>
          <button 
            className="scroll-button right" 
            onClick={() => scrollUpdates('right')}
            aria-label="Scroll right"
          >
            →
          </button>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Equation Live</h4>
            <p>Making LaTeX editing and rendering easy in ChatGPT</p>
          </div>
          <div className="footer-section">
            <ul>
              <li><a href="/support">Support</a></li>
              <li><a href="/about">About</a></li>
              <li><a href="/privacy">Privacy Policy</a></li>
              <li><a href="/terms">Terms of Service</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Equation Live. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default Home;
